import { Box, NativeSelect, Text } from '@chakra-ui/react'
import React, { useEffect, useState } from 'react'

import { ConfirmDialog } from '@mangovoice/mango-ui'
import { ExtensionType } from '@mangovoice/types'

import { useExtensionsI18n } from '../i18n/provider'

export interface LineOptionForReassign {
  id: number
  name: string
  number?: string
}

interface ExtensionDeleteDialogProps {
  isOpen: boolean
  onClose: () => void
  displayName: string
  onConfirm: (reassignToExtensionId?: number | null) => void
  isDeleting: boolean
  extensionType?: string
  otherLines?: LineOptionForReassign[]
  allowReassign?: boolean
}

const REASSIGN_NONE = ''

export const ExtensionDeleteDialog = React.memo<ExtensionDeleteDialogProps>(
  ({
    isOpen,
    onClose,
    displayName,
    onConfirm,
    isDeleting,
    extensionType,
    otherLines = [],
    allowReassign = true,
  }) => {
    const { text } = useExtensionsI18n()
    const isLine = extensionType === ExtensionType.LINE
    const showReassign = allowReassign && isLine && otherLines.length > 0
    const [reassignToExtensionId, setReassignToExtensionId] =
      useState<string>(REASSIGN_NONE)

    useEffect(() => {
      if (!isOpen) setReassignToExtensionId(REASSIGN_NONE)
    }, [isOpen])

    const handleConfirm = () => {
      const value =
        showReassign && reassignToExtensionId !== REASSIGN_NONE
          ? Number(reassignToExtensionId)
          : null
      onConfirm(value ?? undefined)
    }

    return (
      <ConfirmDialog
        open={isOpen}
        onClose={onClose}
        onConfirm={handleConfirm}
        isLoading={isDeleting}
        variant="destructive"
        title={text('extensions.deleteDialog.title', 'Delete Extension')}
        description={text(
          'extensions.deleteDialog.confirmation',
          'Are you sure you want to delete "{{displayName}}"? This action cannot be undone.',
          {
            displayName:
              displayName ||
              text('extensions.deleteDialog.unnamed', 'Unnamed Extension'),
          },
        )}
        confirmLabel={text('extensions.common.delete', 'Delete')}
        cancelLabel={text('extensions.common.cancel', 'Cancel')}
      >
        {showReassign && (
          <Box mt={4}>
            <Text mb={2} fontSize="sm" fontWeight="medium">
              {text(
                'extensions.deleteDialog.reassignLabel',
                'Reassign references to',
              )}
            </Text>
            <NativeSelect.Root size="sm">
              <NativeSelect.Field
                value={reassignToExtensionId}
                onChange={(e) =>
                  setReassignToExtensionId(e.target.value ?? REASSIGN_NONE)
                }
              >
                <option value={REASSIGN_NONE}>
                  {text(
                    'extensions.deleteDialog.deleteWithoutReassigning',
                    'Delete without reassigning',
                  )}
                </option>
                {otherLines.map((line) => (
                  <option key={line.id} value={String(line.id)}>
                    {line.number != null && line.number !== ''
                      ? `${line.number} — ${line.name}`
                      : line.name}
                  </option>
                ))}
              </NativeSelect.Field>
              <NativeSelect.Indicator />
            </NativeSelect.Root>
          </Box>
        )}
      </ConfirmDialog>
    )
  },
)

ExtensionDeleteDialog.displayName = 'ExtensionDeleteDialog'
